import React from 'react';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { GameInfo } from '@/pages/game';
import TeamLogo from './team-logo';
import { Tooltip } from "@heroui/tooltip";
import { isMobile } from '@/stores/MenuStore';
import { Tab, Tabs, Button, ButtonGroup } from "@heroui/react";
import { STAT_KEYS_FIXTURES } from "@/utils/stats";
import { statParamToName } from "@/utils/names"

interface GamesStatsProps {
    gameInfo: GameInfo;
    gameStats: any;
    selectedHalf: 'full-game' | 'first-half' | 'second-half';
    setSelectedHalf: (h: 'full-game' | 'first-half' | 'second-half') => void;
}

const GamesStats: React.FC<GamesStatsProps> = ({ gameInfo, gameStats, selectedHalf, setSelectedHalf }) => {
    const { t } = useTranslation();

    const STAT_GROUPS = {
        Geral: [
            'BALL_POSSESSION',
            'PASSES',
            'ACCURATE_PASSES',
            'TOTAL_CROSSES',
            'ACCURATE_CROSSES',
            'LONG_BALLS',
            'LONG_BALLS_WON',
            'TOUCHES',
            'GOAL_KICKS'
        ],
        Ataque: [
            'GOALS',
            'SHOTS_TOTAL',
            'SHOTS_ON_TARGET',
            'SHOTS_OFF_TARGET',
            'BIG_CHANCES_CREATED',
            'BIG_CHANCES_MISSED',
            'HIT_WOODWORK',
            'OFFSIDES',
            'ASSISTS',
            'KEY_PASSES',
            'SUCCESSFUL_DRIBBLES',
            'DRIBBLED_ATTEMPTS',
            'PENALTIES_SCORED',
            'OWN_GOALS'
        ],
        Defesa: [
            'TACKLES',
            'INTERCEPTIONS',
            'CLEARANCES',
            'BLOCKED_SHOTS',
            'SAVES',
            'SAVES_INSIDE_BOX',
            'ERROR_LEAD_TO_GOAL',
            'AERIALS_WON',
            'AERIALS_LOST',
            'DUELS_TOTAL',
            'DUELS_WON',
            'DUELS_LOST',
            'PENALTIES_SAVED'
        ],
        Disciplina: [
            'FOULS',
            'FOULS_DRAWN',
            'YELLOWCARDS',
            'REDCARDS',
            'YELLOWRED_CARDS',
            'PENALTIES_COMMITTED'
        ]
    };


    return (
        <div className={`py-4 shadow flex w-full flex-col bg-default-100 rounded-xl fadeIn self-start ${!isMobile() && 'max-w-sm min-w-xs'}`}>
            <div className="flex pb-4 items-center justify-between px-5">
                <p className="min-w-[130px] text-lg font-medium text-default-700">{t('game.game_3')}</p>
                <Tooltip content={gameInfo.home_name}>
                    <Link to={`/team/${gameInfo.home_id}`} key={gameInfo.home_id} className="flex w-full justify-center">
                        <TeamLogo className='mx-1' teamId={gameInfo.home_id} size={25} />
                    </Link>
                </Tooltip>
                <Tooltip content={gameInfo.away_name}>
                    <Link to={`/team/${gameInfo.away_id}`} key={gameInfo.away_id} className="flex w-full justify-center pe-2">
                        <TeamLogo className='mx-1' teamId={gameInfo.away_id} size={25} />
                    </Link>
                </Tooltip>
            </div>

            <ButtonGroup className="flex items-center justify-start px-5 pb-2">
                {(['full-game', 'first-half', 'second-half'] as const).map((half, i) => (
                    <Button key={half+i} className={`${selectedHalf==half ? "bg-primary-500" : ""} h-[34px]`} onClick={()=>setSelectedHalf(half)}>
                        {half == 'full-game' ? t('game.game_13') : half == 'first-half' ? t('game.game_14') : t('game.game_15')}
                    </Button>
                ))}
            </ButtonGroup>

            <div className="flex flex-col">
                <Tabs aria-label={t('game.game_4')} color="primary" className="px-4">
                    {Object.entries(STAT_GROUPS).map(([groupName, statsInGroup], idx) => (
                        <Tab key={groupName} title={t(`game.game_${5 + idx}`)}>
                            <div>
                                {statsInGroup.filter(stat => STAT_KEYS_FIXTURES.includes(stat)).map((stat) => (
                                    <div key={stat} className="flex w-full items-center justify-between text-default-700 hover:bg-default-300 py-1 px-7 transition">
                                        <span className="min-w-[122px] text-sm text-default-500">{t(statParamToName(stat))}</span>
                                        <span className="w-full font-medium text-center">
                                            {gameStats?.data?.home?.[stat] ?? 0}{stat === 'BALL_POSSESSION' && '%'}
                                        </span>
                                        <span className="w-full font-medium text-center">
                                            {gameStats?.data?.away?.[stat] ?? 0}{stat === 'BALL_POSSESSION' && '%'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Tab>
                    ))}
                </Tabs>
            </div>
        </div>
    );
};

export default GamesStats;
